import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageTitle } from "../components/PageTitle";
import { getProducts, getFilters } from "../api/products";
import { getImageUrl } from "../api/utils";
import {
  getWindowDimensions,
  OK,
  showErrorMessage,
  sizesLabel,
  stripEmojis,
  shuffleArray,
} from "../utils";
import ProductFilters from "../components/ProductFilters";
import ProductCard from "../components/ProductCard";
import StarRating from "../components/StarRating";

const FALLBACK_IMAGE = "/assets/images/product-placeholder.svg";

// ── Single Product Card ───────────────────────────────────────

// ── Loading Skeleton ──────────────────────────────────────────
const ProductSkeleton = ({ perRowClass }) => (
  <div className={`card-product ${perRowClass} col-md-6 col-sm-6 col-xs-6`}>
    <div className="card-product-wrapper">
      <div
        style={{
          background: "#f4f3ef",
          paddingTop: "120%",
          animation: "pulse 1.4s ease-in-out infinite",
        }}
      />
    </div>
    <div className="card-product-info" style={{ padding: "12px 0" }}>
      <div
        style={{
          background: "#f4f3ef",
          height: 14,
          width: "70%",
          marginBottom: 8,
          animation: "pulse 1.4s ease-in-out infinite",
        }}
      />
      <div
        style={{
          background: "#f4f3ef",
          height: 14,
          width: "40%",
          animation: "pulse 1.4s ease-in-out infinite",
        }}
      />
    </div>
  </div>
);

// ── Main Products Page ────────────────────────────────────────
const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState(
    () => searchParams.get("sort") || "default",
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [productTypesLoading, setProductTypesLoading] = useState(false);
  const [colorNameMap, setColorNameMap] = useState({});
  const [perRowClass, setPerRowClass] = useState("col-lg-4");
  const [priceDefaults, setPriceDefaults] = useState([0, 5000]);
  const [activeFilters, setActiveFilters] = useState({
    product_types: [],
    genders: [],
    ages: [],
    collections: [],
    sizes: [],
    colors: [],
    priceRange: [0, 5000],
  });

  const [localSearch, setLocalSearch] = useState(
    () => searchParams.get("search") || "",
  );
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get("search") || "",
  );

  const sentinelRef = useRef(null);
  const isFetchingRef = useRef(false);
  const filtersRef = useRef(activeFilters);
  const scrollPosRef = useRef(0);

  const normalizeProductTypes = (res) => {
    const raw =
      res?.product_types ||
      res?.productTypes ||
      res?.types ||
      res?.categories ||
      res?.filters?.product_types ||
      res?.data?.product_types ||
      [];

    if (!Array.isArray(raw)) return [];

    return raw
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") {
          return { label: item, value: item, count: null };
        }
        return {
          label:
            item.name ||
            item.title ||
            item.label ||
            item.product_type ||
            item.value ||
            "Product type",
          value:
            item.slug ||
            item.code ||
            item.product_type ||
            item.value ||
            item.id ||
            item.name ||
            item.title ||
            "",
          count:
            item.count ??
            item.total ??
            item.products_count ??
            item.product_count ??
            null,
        };
      })
      .filter((item) => item?.label);
  };

  const isProductOnSale = (product) => {
    if (!product) return false;

    const sizeSaleExists = Array.isArray(product.product_sizes)
      ? product.product_sizes.some((size) => {
          const selling = Number(size?.price ?? size?.selling_price ?? 0);
          const original = Number(
            size?.original_price ??
              size?.mrp ??
              size?.compare_at_price ??
              size?.regular_price ??
              size?.compare_price ??
              0,
          );
          return original > 0 && selling > 0 && original > selling;
        })
      : false;

    return Boolean(
      product.is_sale ||
      product.isSale ||
      product.on_sale ||
      product.sale_price ||
      product.discount_price ||
      product.compare_price > product.price ||
      product.comparePrice > product.price ||
      product.sale_percent ||
      product.sale_percentage ||
      product.discount_percent ||
      sizeSaleExists ||
      Number(
        product.compare_price ||
          product.comparePrice ||
          product.compare_at_price ||
          product.original_price ||
          0,
      ) > Number(product.price || 0),
    );
  };

  const getProductImage = (product) =>
    getImageUrl(
      product?.variant_images?.[0]?.image ||
        product?.display_images?.[0]?.image ||
        FALLBACK_IMAGE,
    );

  const getRandomSaleProducts = (list = []) => {
    const saleItems = list.filter(isProductOnSale);
    if (saleItems.length <= 3) return saleItems;

    const shuffled = [...saleItems].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  };

  const getRandomGalleryProducts = (list = []) => {
    if (list.length <= 6) return list;
    const shuffled = [...list].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 6);
  };

  // Read URL params on mount (gender, category, search, and all filters)
  const gender = searchParams.get("gender") || "";
  const category = searchParams.get("category") || "";
  const search = searchParams.get("search") || "";
  const productType = searchParams.get("product_type") || "";
  const age = searchParams.get("age") || "";
  const collections = searchParams.get("collections") || "";
  const sizes = searchParams.get("sizes") || "";
  const colors = searchParams.get("colors") || "";
  const priceRangeParam = searchParams.get("priceRange") || "";

  useEffect(() => {
    const priceRangeFromUrl = priceRangeParam
      ? priceRangeParam.split(",").map(Number)
      : priceDefaults;

    const nextFilters = {
      product_types: productType ? [productType] : [],
      genders: gender ? [gender] : [],
      ages: age ? [age] : [],
      collections: collections ? collections.split(",") : [],
      sizes: sizes ? sizes.split(",") : [],
      colors: colors ? colors.split(",") : [],
      priceRange: priceRangeFromUrl,
    };

    filtersRef.current = nextFilters;
    setActiveFilters(nextFilters);
    setSearchQuery(search);
    setLocalSearch(search);
    setCurrentPage(1);
    setHasMore(true);
    isFetchingRef.current = false;
  }, [
    gender,
    productType,
    category,
    search,
    age,
    collections,
    sizes,
    colors,
    priceRangeParam,
    priceDefaults,
  ]);

  // Reset on filter change
  const handleFilterChange = (newFilters) => {
    filtersRef.current = newFilters;
    setActiveFilters(newFilters);
    setProducts([]);
    setCurrentPage(1);
    setHasMore(true);

    // Save scroll position before updating params
    scrollPosRef.current = window.scrollY || document.documentElement.scrollTop;

    // Update URL params
    const newParams = new URLSearchParams(searchParams);

    // Update filter-related params
    if (newFilters.product_types.length > 0) {
      newParams.set("product_type", newFilters.product_types[0]);
    } else {
      newParams.delete("product_type");
    }

    if (newFilters.genders.length > 0) {
      newParams.set("gender", newFilters.genders[0]);
    } else {
      newParams.delete("gender");
    }

    if (newFilters.ages.length > 0) {
      newParams.set("age", newFilters.ages[0]);
    } else {
      newParams.delete("age");
    }

    if (newFilters.collections.length > 0) {
      newParams.set("collections", newFilters.collections.join(","));
    } else {
      newParams.delete("collections");
    }

    if (newFilters.sizes.length > 0) {
      newParams.set("sizes", newFilters.sizes.join(","));
    } else {
      newParams.delete("sizes");
    }

    if (newFilters.colors.length > 0) {
      newParams.set("colors", newFilters.colors.join(","));
    } else {
      newParams.delete("colors");
    }

    if (
      newFilters.priceRange[0] !== priceDefaults[0] ||
      newFilters.priceRange[1] !== priceDefaults[1]
    ) {
      newParams.set(
        "priceRange",
        `${newFilters.priceRange[0]},${newFilters.priceRange[1]}`,
      );
    } else {
      newParams.delete("priceRange");
    }

    setSearchParams(newParams);

    // Restore scroll position after params update
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosRef.current);
    });
  };

  const handleProductTypeClick = (type) => {
    const value = type?.value || "";
    if (!value) return;

    handleFilterChange({
      ...activeFilters,
      product_types: [value],
    });
  };

  const removeFilterValue = (key, value) => {
    const nextValues = (activeFilters[key] || []).filter(
      (item) => item !== value,
    );
    handleFilterChange({
      ...activeFilters,
      [key]: nextValues,
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedSearch = localSearch.trim();
    setSearchQuery(trimmedSearch);
    setCurrentPage(1);
    setHasMore(true);
    isFetchingRef.current = false;

    // Save scroll position before updating params
    scrollPosRef.current = window.scrollY || document.documentElement.scrollTop;

    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (trimmedSearch) {
      newParams.set("search", trimmedSearch);
    } else {
      newParams.delete("search");
    }
    setSearchParams(newParams);

    // Restore scroll position after params update
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosRef.current);
    });
  };

  const handleSearchClear = () => {
    setLocalSearch("");
    setSearchQuery("");
    setCurrentPage(1);
    setHasMore(true);
    isFetchingRef.current = false;

    // Save scroll position before updating params
    scrollPosRef.current = window.scrollY || document.documentElement.scrollTop;

    // Clear search from URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("search");
    setSearchParams(newParams);

    // Restore scroll position after params update
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosRef.current);
    });
  };

  const clearAllFilters = () => {
    setLocalSearch("");
    setSearchQuery("");

    // Save scroll position
    scrollPosRef.current = window.scrollY || document.documentElement.scrollTop;

    // Clear all URL params at once
    const newParams = new URLSearchParams();
    setSearchParams(newParams);

    // Update filter state
    filtersRef.current = {
      product_types: [],
      genders: [],
      ages: [],
      collections: [],
      sizes: [],
      colors: [],
      priceRange: priceDefaults,
    };
    setActiveFilters(filtersRef.current);
    setProducts([]);
    setCurrentPage(1);
    setHasMore(true);

    // Restore scroll position
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosRef.current);
    });
  };

  const activeChips = [
    ...(searchQuery
      ? [
          {
            key: "search",
            label: `Search: "${searchQuery}"`,
            onRemove: handleSearchClear,
          },
        ]
      : []),
    ...(activeFilters.priceRange?.[0] !== priceDefaults[0] ||
    activeFilters.priceRange?.[1] !== priceDefaults[1]
      ? [
          {
            key: `price:${activeFilters.priceRange?.[0]}-${activeFilters.priceRange?.[1]}`,
            label: `Price: ₹${activeFilters.priceRange?.[0]} - ₹${activeFilters.priceRange?.[1]}`,
            onRemove: () =>
              handleFilterChange({
                ...activeFilters,
                priceRange: priceDefaults,
              }),
          },
        ]
      : []),
    ...(activeFilters.product_types || []).map((value) => ({
      key: `product_types:${value}`,
      label: `Type: ${value}`,
      onRemove: () => removeFilterValue("product_types", value),
    })),
    ...(activeFilters.genders || []).map((value) => ({
      key: `genders:${value}`,
      label: `Gender: ${value}`,
      onRemove: () => removeFilterValue("genders", value),
    })),
    ...(activeFilters.ages || []).map((value) => ({
      key: `ages:${value}`,
      label: `Age: ${value}`,
      onRemove: () => removeFilterValue("ages", value),
    })),
    ...(activeFilters.collections || []).map((value) => ({
      key: `collections:${value}`,
      label: `Collection: ${value}`,
      onRemove: () => removeFilterValue("collections", value),
    })),
    ...(activeFilters.sizes || []).map((value) => ({
      key: `sizes:${value}`,
      label: `Size: ${value}`,
      onRemove: () => removeFilterValue("sizes", value),
    })),
    ...(activeFilters.colors || []).map((value) => ({
      key: `colors:${value}`,
      label: `Color: ${colorNameMap[value] || value}`,
      onRemove: () => removeFilterValue("colors", value),
    })),
  ];

  const saleProducts = useMemo(
    () => getRandomSaleProducts(products),
    [products],
  );
  const galleryProducts = useMemo(
    () => getRandomGalleryProducts(products),
    [products],
  );

  useEffect(() => {
    loadProducts(currentPage);
  }, [currentPage, activeFilters, sortBy, gender, category, searchQuery, age]);

  useEffect(() => {
    let alive = true;

    const loadProductTypes = async () => {
      try {
        setProductTypesLoading(true);
        const res = await getFilters();
        if (!alive) return;
        setProductTypes(normalizeProductTypes(res));
        const minPrice = Number(res?.price_range?.min);
        const maxPrice = Number(res?.price_range?.max);
        const nextPriceDefaults = [
          Number.isFinite(minPrice) ? minPrice : 0,
          Number.isFinite(maxPrice) ? maxPrice : 5000,
        ];
        setPriceDefaults(nextPriceDefaults);

        setActiveFilters((prev) => {
          const isStillInitialPrice =
            prev.priceRange?.[0] === 0 && prev.priceRange?.[1] === 5000;
          const nextFilters = isStillInitialPrice
            ? { ...prev, priceRange: nextPriceDefaults }
            : prev;
          filtersRef.current = nextFilters;
          return nextFilters;
        });
        const nextColorMap = {};
        (res?.colors || []).forEach((colorItem) => {
          if (!colorItem) return;
          const hex =
            colorItem.color || colorItem.hex_code || colorItem.hexCode;
          const name = colorItem.name || colorItem.label;
          if (hex && name) nextColorMap[hex] = name;
        });
        setColorNameMap(nextColorMap);
      } catch {
        if (!alive) return;
        setProductTypes([]);
        setColorNameMap({});
      } finally {
        if (alive) setProductTypesLoading(false);
      }
    };

    loadProductTypes();

    return () => {
      alive = false;
    };
  }, []);

  const loadProducts = async (page) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    const hasExisting = products.length > 0;
    page === 1 && !hasExisting ? setLoading(true) : setLoadingMore(true);

    try {
      const f = filtersRef.current;
      const res = await getProducts(page, {
        product_type: (f.product_types || []).join(","),
        gender: (f.genders || []).join(","),
        age: (f.ages || []).join(","),
        collection_id: (f.collections || []).join(","),
        sizes: f.sizes.join(","),
        colors: f.colors.join(","),
        price_min: f.priceRange[0],
        price_max: f.priceRange[1],
        sort: sortBy,
        query: searchQuery,
        category_id: category,
        per_page: 100,
      });

      if (res?.status === OK) {
        const rawProducts = res?.products || [];
        const next = searchParams.get("sort")
          ? rawProducts
          : shuffleArray(rawProducts);

        setProducts((prev) => {
          if (page === 1) return next;
          const ids = new Set(prev.map((p) => p.id));
          return [...prev, ...next.filter((p) => !ids.has(p.id))];
        });
        const totalPages = res?.pagination?.total_pages || 1;
        setHasMore(page < totalPages && next.length > 0);
      }
    } catch (err) {
      showErrorMessage(err || "Failed to load products");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  };

  // ── Infinite Scroll ───────────────────────────────────────
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingRef.current) {
          setCurrentPage((p) => p + 1);
        }
      },
      { rootMargin: "220px 0px" },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, products.length]);

  return (
    <>
      <PageTitle
        title="Products"
        subtitle="Find Your Hue!"
        bgImage="shopBanner.png"
      />

      <section className="flat-spacing-1">
        <div className="container">
          {/* ── Top Controls — desktop (grid-3: filter | search | sort) ── */}
          <div className="d-none d-md-block">
            <div className="tf-shop-control grid-3 align-items-center">
              <a onClick={() => setFilterOpen(true)}>
                <div className="tf-control-filter">
                  <span className="tf-btn-filter">
                    <span className="icon icon-filter" />
                    <span className="text">Filter</span>
                  </span>
                </div>
              </a>

              <form
                className="tf-mini-search-frm"
                onSubmit={handleSearchSubmit}
              >
                <fieldset className="text">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                  />
                </fieldset>
                <button type="submit">
                  <i className="icon-search" />
                </button>
              </form>

              <div className="tf-control-sorting d-flex justify-content-end">
                <select
                  value={sortBy}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSortBy(val);
                    setProducts([]);
                    setCurrentPage(1);
                    setHasMore(true);

                    // ── URL param update ──────────────────────────
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set("sort", val);

                    setSearchParams(newParams);
                  }}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px 12px",
                    fontSize: 13,
                    color: "#2c3a34",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <option value="default">Default sorting</option>
                  <option value="newest">Sort by latest</option>
                  <option value="oldest">Sort by oldest</option>
                  <option value="on_sale">On Sale</option>
                  <option value="price_asc">Price: low to high</option>
                  <option value="price_desc">Price: high to low</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Top Controls — mobile (grid-2: filter | sort) + full-width search ── */}
          <div className="d-md-none">
            <div className="tf-shop-control grid-2 align-items-center">
              <a onClick={() => setFilterOpen(true)}>
                <div className="tf-control-filter">
                  <span className="tf-btn-filter">
                    <span className="icon icon-filter" />
                    <span className="text">Filter</span>
                  </span>
                </div>
              </a>
              <div className="tf-control-sorting d-flex justify-content-end">
                <select
                  value={sortBy}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSortBy(val);
                    setProducts([]);
                    setCurrentPage(1);
                    setHasMore(true);

                    // ── URL param update ──────────────────────────
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set("sort", val);

                    setSearchParams(newParams);
                  }}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px 12px",
                    fontSize: 13,
                    color: "#2c3a34",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <option value="default">Default sorting</option>
                  <option value="newest">Sort by latest</option>
                  <option value="oldest">Sort by oldest</option>
                  <option value="on_sale">On Sale</option>
                  <option value="price_asc">Price: low to high</option>
                  <option value="price_desc">Price: high to low</option>
                </select>
              </div>
            </div>
            <form
              className="tf-mini-search-frm"
              onSubmit={handleSearchSubmit}
              style={{ margin: "10px 0 16px" }}
            >
              <fieldset className="text">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                />
              </fieldset>
              <button type="submit">
                <i className="icon-search" />
              </button>
            </form>
          </div>

          {activeChips.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
                margin: "0 0 18px",
              }}
            >
              {activeChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={chip.onRemove}
                  style={{
                    border: "1px solid #ddd",
                    background: "#fff",
                    borderRadius: 999,
                    padding: "8px 12px",
                    fontSize: 12,
                    color: "#2c3a34",
                    cursor: "pointer",
                  }}
                >
                  {chip.label.startsWith("Size:")
                    ? `Size: ${sizesLabel[chip.label.replace("Size: ", "")]}`
                    : chip.label}{" "}
                  ×
                </button>
              ))}
              <button
                type="button"
                onClick={clearAllFilters}
                style={{
                  border: "1px solid #000",
                  background: "#000",
                  borderRadius: 999,
                  padding: "8px 12px",
                  fontSize: 12,
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Clear all
              </button>
            </div>
          )}

          <div class="tf-row-flex">
            {getWindowDimensions().width >= 1368 && (
              <aside className="col-lg-3 tf-shop-sidebar wrap-sidebar-mobile">
                <div className="widget-facet wd-categories">
                  <div
                    className="facet-title"
                    data-bs-target="#categories"
                    data-bs-toggle="collapse"
                    aria-expanded="true"
                    aria-controls="categories"
                  >
                    <span>Product Types</span>
                    <span className="icon icon-arrow-up" />
                  </div>
                  <div id="categories" className="collapse show">
                    <ul className="list-categoris current-scrollbar mb_36">
                      {productTypesLoading ? (
                        <li className="cate-item current">
                          <span>Loading product types...</span>
                        </li>
                      ) : productTypes.length > 0 ? (
                        productTypes.map((type, index) => (
                          <li
                            className={`cate-item ${index === 0 ? "current" : ""}`}
                            key={`${type.value || type.label}-${index}`}
                          >
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleProductTypeClick(type);
                              }}
                              aria-current={
                                activeFilters.product_types.includes(type.value)
                                  ? "true"
                                  : undefined
                              }
                              style={{
                                fontWeight:
                                  activeFilters.product_types.includes(
                                    type.value,
                                  )
                                    ? 600
                                    : 400,
                              }}
                            >
                              <span>{type.label}</span>
                              {type.count !== null &&
                              type.count !== undefined ? (
                                <>
                                  &nbsp;<span>({type.count})</span>
                                </>
                              ) : null}
                            </a>
                          </li>
                        ))
                      ) : (
                        <li className="cate-item current">
                          <span>No product types found</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="widget-facet">
                  <div
                    className="facet-title"
                    data-bs-target="#sale-products"
                    data-bs-toggle="collapse"
                    aria-expanded="true"
                    aria-controls="sale-products"
                  >
                    <span>Sale products</span>
                    <span className="icon icon-arrow-up" />
                  </div>
                  <div id="sale-products" className="collapse show">
                    <div className="widget-featured-products mb_36">
                      {saleProducts.length > 0 ? (
                        saleProducts.map((product) => {
                          const image = getProductImage(product);
                          const title = stripEmojis(
                            product.name || product.title || "Product",
                          );
                          const price = Number(product.price || 0);

                          return (
                            <div
                              className="featured-product-item"
                              key={product.id}
                            >
                              <a
                                href={
                                  product.slug
                                    ? `/products/${product.slug}`
                                    : "#"
                                }
                                className="card-product-wrapper"
                                onClick={(e) => {
                                  if (!product.slug) e.preventDefault();
                                }}
                              >
                                <img
                                  className="img-product"
                                  src={image}
                                  alt={title}
                                  loading="lazy"
                                />
                              </a>
                              <div className="card-product-info">
                                <a
                                  href={
                                    product.slug
                                      ? `/products/${product.slug}`
                                      : "#"
                                  }
                                  className="title link"
                                  onClick={(e) => {
                                    if (!product.slug) e.preventDefault();
                                  }}
                                >
                                  {title}
                                </a>
                                <span className="price">
                                  ₹{price.toFixed(0)}
                                  {product.compare_price > product.price && (
                                    <span
                                      className="compare-at-price"
                                      style={{
                                        marginLeft: 8,
                                        fontSize: 12,
                                        color: "#aaa",
                                        textDecoration: "line-through",
                                      }}
                                    >
                                      ₹
                                      {Number(
                                        product.compare_price,
                                      ).toLocaleString("en-IN")}
                                    </span>
                                  )}
                                </span>

                                <StarRating
                                  rating={Number(product.avg_rating ?? 0)}
                                  count={product.reviews_count ?? 0}
                                />
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="featured-product-item">
                          <div className="card-product-info">
                            <span className="title link">
                              No sale products found
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="widget-facet">
                  <div
                    className="facet-title"
                    data-bs-target="#shipping"
                    data-bs-toggle="collapse"
                    aria-expanded="true"
                    aria-controls="shipping"
                  >
                    <span>Shipping &amp; Delivery</span>
                    <span className="icon icon-arrow-up" />
                  </div>
                  <div id="shipping" className="collapse show">
                    <ul className="widget-iconbox-list mb_36">
                      <li className="iconbox-item">
                        <div className="box-icon w_50 round">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={16}
                            viewBox="0 0 24 16"
                            fill="none"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0 1C0 0.447715 0.447715 0 1 0H15.5C16.0523 0 16.5 0.447715 16.5 1V3.5H19.7857C20.099 3.5 20.3943 3.64687 20.5833 3.89679L23.7976 8.14679C23.9289 8.32046 24 8.53225 24 8.75V13C24 13.5523 23.5523 14 23 14H20.3293C19.9174 15.1652 18.8062 16 17.5 16C16.1938 16 15.0826 15.1652 14.6707 14H8.82929C8.41745 15.1652 7.3062 16 6 16C4.69378 16 3.58255 15.1652 3.17071 14H1C0.447715 14 0 13.5523 0 13V1ZM3.17071 12C3.58255 10.8348 4.69378 10 6 10C7.3062 10 8.41745 10.8348 8.82929 12H14.5V2H2V12H3.17071ZM16.5 10.1707V5.5H19.2882L22 9.08557V12H20.3293C19.9174 10.8348 18.8062 10 17.5 10C17.1494 10 16.8128 10.0602 16.5 10.1707ZM6 12C5.44772 12 5 12.4477 5 13C5 13.5523 5.44772 14 6 14C6.55227 14 7 13.5523 7 13C7 12.4477 6.55227 12 6 12ZM17.5 12C16.9477 12 16.5 12.4477 16.5 13C16.5 13.5523 16.9477 14 17.5 14C18.0523 14 18.5 13.5523 18.5 13C18.5 12.4477 18.0523 12 17.5 12Z"
                              fill="black"
                            />
                          </svg>
                        </div>
                        <div className="iconbox-content">
                          <h4 className="iconbox-title">Free shipping</h4>
                          <p className="iconbox-desc">
                            Free Shipping on orders ₹499+
                          </p>
                        </div>
                      </li>
                      <li className="iconbox-item">
                        <div className="box-icon w_50 round">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M14.9074 23.9572C13.9179 23.9561 12.9418 23.7273 12.0548 23.2887C9.58879 22.0801 7.33175 20.4851 5.36912 18.5641C3.44812 16.6015 1.85314 14.3444 0.644541 11.8784C0.0604334 10.6753 -0.133278 9.31981 0.0904706 8.0013C0.314219 6.68278 0.94425 5.46709 1.89254 4.52406L3.43026 2.98635C3.79573 2.62231 4.29056 2.41791 4.8064 2.41791C5.32225 2.41791 5.81707 2.62231 6.18255 2.98635L8.47798 5.29292C8.80356 5.62725 8.99924 6.06673 9.02988 6.5324C9.06052 6.99806 8.92409 7.45939 8.64512 7.83349L7.53084 9.3935C8.04739 11.0793 8.9739 12.6107 10.2274 13.8506C11.4467 15.0497 12.9375 15.9366 14.5731 16.4358L16.1331 15.3215C16.5094 15.041 16.9738 14.9047 17.4421 14.9374C17.9103 14.9701 18.3512 15.1696 18.6849 15.4998L20.9803 17.7952C21.3443 18.1607 21.5487 18.6555 21.5487 19.1714C21.5487 19.6872 21.3443 20.182 20.9803 20.5475L19.4426 22.0852C18.8467 22.6799 18.1394 23.1513 17.3613 23.4725C16.5831 23.7937 15.7493 23.9584 14.9074 23.9572ZM4.81197 4.08949C4.77414 4.08932 4.7367 4.09717 4.70212 4.11254C4.66755 4.1279 4.63663 4.15043 4.6114 4.17863L3.07369 5.70521C2.36974 6.40573 1.90384 7.30983 1.74187 8.28965C1.5799 9.26947 1.73006 10.2754 2.17111 11.1652C3.32229 13.4583 4.82631 15.5564 6.62826 17.3829C8.45481 19.1849 10.5529 20.6889 12.846 21.8401C13.7358 22.2811 14.7417 22.4313 15.7215 22.2693C16.7014 22.1074 17.6055 21.6415 18.306 20.9375L19.788 19.3552C19.8146 19.3302 19.8359 19.3 19.8504 19.2665C19.8649 19.2329 19.8724 19.1968 19.8724 19.1602C19.8724 19.1237 19.8649 19.0875 19.8504 19.054C19.8359 19.0204 19.8146 18.9902 19.788 18.9652L17.5594 16.6698C17.5086 16.6251 17.4432 16.6005 17.3756 16.6005C17.3079 16.6005 17.2425 16.6251 17.1917 16.6698L15.3197 18.0849C15.2278 18.1524 15.123 18.2002 15.0117 18.2251C14.9005 18.25 14.7853 18.2516 14.6734 18.2298C12.5479 17.7079 10.6152 16.5908 9.10198 15.0095C7.46364 13.4857 6.30245 11.5194 5.75912 9.34893C5.73795 9.23701 5.73988 9.12196 5.76479 9.01082C5.7897 8.89968 5.83707 8.79481 5.90398 8.70264L7.31912 6.83064C7.36378 6.7798 7.38841 6.71445 7.38841 6.64678C7.38841 6.57911 7.36378 6.51376 7.31912 6.46292L5.0014 4.17863C4.97837 4.15071 4.94944 4.12824 4.91668 4.11282C4.88393 4.09741 4.84817 4.08944 4.81197 4.08949ZM22.8857 14.2072C23.611 12.833 23.9933 11.3039 24 9.75007C24 8.46686 23.7467 7.19627 23.2546 6.01116C22.7626 4.82605 22.0414 3.74973 21.1325 2.84392C20.2236 1.93811 19.1448 1.22063 17.958 0.732616C16.7712 0.244602 15.4998 -0.00434226 14.2166 5.73114e-05C12.6627 0.00678991 11.1336 0.389078 9.75941 1.11434C9.57002 1.2205 9.42792 1.39451 9.36175 1.60129C9.29558 1.80807 9.31026 2.03225 9.40284 2.22863C9.50889 2.41806 9.68365 2.55953 9.89102 2.62381C10.0984 2.6881 10.3225 2.67029 10.5171 2.57406C11.6525 1.98346 12.9144 1.67754 14.1943 1.68263C16.3379 1.67967 18.395 2.52761 19.9139 4.04023C21.4328 5.55285 22.2892 7.60647 22.2951 9.75007C22.3002 11.0299 21.9943 12.2918 21.4037 13.4272C21.3075 13.6218 21.2897 13.846 21.354 14.0533C21.4182 14.2607 21.5597 14.4354 21.7491 14.5415C21.8713 14.5986 22.0043 14.629 22.1391 14.6306C22.2898 14.6318 22.4381 14.5933 22.5692 14.519C22.7002 14.4447 22.8094 14.3371 22.8857 14.2072ZM16.4451 11.9786C16.5822 11.8254 16.6579 11.6271 16.6579 11.4215C16.6579 11.2159 16.5822 11.0176 16.4451 10.8644L15.0523 9.40464V6.40721C15.0523 6.18556 14.9642 5.97299 14.8075 5.81627C14.6508 5.65954 14.4382 5.57149 14.2166 5.57149C13.9949 5.57149 13.7823 5.65954 13.6256 5.81627C13.4689 5.97299 13.3808 6.18556 13.3808 6.40721V9.75007C13.381 9.97164 13.4692 10.1841 13.626 10.3406L15.3308 11.9786C15.4841 12.1157 15.6824 12.1915 15.888 12.1915C16.0936 12.1915 16.2919 12.1157 16.4451 11.9786Z"
                              fill="black"
                            />
                          </svg>
                        </div>
                        <div className="iconbox-content">
                          <h4 className="iconbox-title">Premium Support</h4>
                          <p className="iconbox-desc">Support 24 hours a day</p>
                        </div>
                      </li>
                      <li className="iconbox-item">
                        <div className="box-icon w_50 round">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={22}
                            height={22}
                            viewBox="0 0 22 22"
                            fill="none"
                          >
                            <rect
                              x="4"
                              y="7"
                              width="9.5"
                              height="6.5"
                              rx="1.5"
                              stroke="black"
                              strokeWidth="1.8"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5.5 9.9H13"
                              stroke="black"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                            <path
                              d="M8.2 7V4.5H5.7"
                              stroke="black"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8 11.5H11"
                              stroke="black"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                            <circle
                              cx="15.5"
                              cy="8.5"
                              r="3.8"
                              stroke="black"
                              strokeWidth="1.8"
                            />
                            <path
                              d="M14.2 8.45H16.2C16.55 8.45 16.85 8.73 16.85 9.08C16.85 9.43 16.55 9.7 16.2 9.7H14.9C14.55 9.7 14.25 9.98 14.25 10.33C14.25 10.68 14.55 10.95 14.9 10.95H16.55"
                              stroke="black"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M13.6 13.6L14.85 12.35L15.75 13.25L17.5 11.25"
                              stroke="black"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="iconbox-content">
                          <h4 className="iconbox-title">Cash on Delivery</h4>
                          <p className="iconbox-desc">
                            Pay When It Arrives. No Trust Issues!
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="widget-facet">
                  <div
                    className="facet-title"
                    data-bs-target="#gallery"
                    data-bs-toggle="collapse"
                    aria-expanded="true"
                    aria-controls="gallery"
                  >
                    <span>Gallery</span>
                    <span className="icon icon-arrow-up" />
                  </div>
                  <div id="gallery" className="collapse show">
                    <div className="grid-3 gap-4 mb_36">
                      {galleryProducts.map((product, index) => {
                        const image = getProductImage(product);
                        const title = stripEmojis(
                          product.name || product.title || "Product",
                        );
                        return (
                          <a
                            href={
                              product.slug ? `/products/${product.slug}` : "#"
                            }
                            className="item-gallery"
                            key={`${product.id || index}`}
                            onClick={(e) => {
                              if (!product.slug) e.preventDefault();
                            }}
                          >
                            <img
                              className="img-fluid"
                              src={image}
                              style={{ height: "100%", width: "100%" }}
                              alt={title}
                              loading="lazy"
                            />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="widget-facet">
                  <div
                    className="facet-title"
                    data-bs-target="#follow"
                    data-bs-toggle="collapse"
                    aria-expanded="true"
                    aria-controls="follow"
                  >
                    <span>Follow us</span>
                    <span className="icon icon-arrow-up" />
                  </div>
                  <div id="follow" className="collapse show">
                    <ul className="tf-social-icon d-flex gap-10">
                      <li>
                        <a
                          href="https://facebook.com/huehoppers.store"
                          className="box-icon w_34 round bg_line social-facebook"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <i className="icon fs-14 icon-fb" />
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://instagram.com/huehoppers26"
                          className="box-icon w_34 round bg_line social-instagram"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <i className="icon fs-14 icon-instagram" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </aside>
            )}

            {/* ── Filter Sidebar (floating drawer) ── */}

            {/* ── Products Grid ── */}
            {loading ? (
              <div className="row" style={{ width: "100%" }}>
                {[...Array(8)].map((_, i) => (
                  <ProductSkeleton
                    perRowClass={perRowClass}
                    key={`initial-${i}`}
                  />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div
                className="wrapper-control-shop tf-shop-content"
                style={{
                  minHeight: "420px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: "560px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    margin: "0 auto",
                  }}
                >
                  <img
                    src={"/assets/images/noResults.png"}
                    alt="No results"
                    style={{ width: 300, marginBottom: 24 }}
                  />
                  <p
                    style={{
                      fontSize: 36,
                      fontWeight: 600,
                      color: "#2c3a34",
                      marginBottom: 8,
                    }}
                  >
                    No products found!
                  </p>
                  <br />
                  <p style={{ fontSize: 24, color: "#888" }}>
                    Try adjusting your filters or{" "}
                    <Link
                      to="/collections"
                      style={{ color: "#2c3a34", fontWeight: 600 }}
                    >
                      browse collections
                    </Link>
                    .
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="row wrapper-shop tf-col-4" id="gridLayout">
                  {getWindowDimensions().width >= 1368 && (
                    <div className="col-lg-12">
                      <ul className="tf-control-layout d-flex justify-content-center">
                        <li
                          onClick={() => setPerRowClass("col-lg-6")}
                          className={`tf-view-layout-switch sw-layout-2 ${perRowClass === "col-lg-6" ? "active" : ""}`}
                          data-value-layout="tf-col-2"
                        >
                          <div className="item">
                            <span className="icon icon-grid-2" />
                          </div>
                        </li>
                        <li
                          onClick={() => setPerRowClass("col-lg-4")}
                          className={`tf-view-layout-switch sw-layout-3 ${perRowClass === "col-lg-4" ? "active" : ""}`}
                          data-value-layout="tf-col-3"
                        >
                          <div className="item">
                            <span className="icon icon-grid-3" />
                          </div>
                        </li>
                        <li
                          onClick={() => setPerRowClass("col-lg-3")}
                          className={`tf-view-layout-switch sw-layout-4 ${perRowClass === "col-lg-3" ? "active" : ""}`}
                          data-value-layout="tf-col-4"
                        >
                          <div className="item">
                            <span className="icon icon-grid-4" />
                          </div>
                        </li>
                      </ul>
                    </div>
                  )}
                  {/* Product cards */}
                  {!loading &&
                    products.map((product) => (
                      <ProductCard
                        perRowClass={perRowClass}
                        key={product.id}
                        product={product}
                      />
                    ))}

                  {/* Load more indicator */}
                  {loadingMore &&
                    [...Array(3)].map((_, i) => (
                      <ProductSkeleton
                        perRowClass={perRowClass}
                        key={`more-${i}`}
                      />
                    ))}
                </div>
              </>
            )}
          </div>

          {/* ── Infinite Scroll Sentinel ── */}
          {!loading && hasMore && (
            <div ref={sentinelRef} style={{ height: 1, width: 1 }} />
          )}

          {/* End of products */}
          {!loading && !hasMore && products.length > 0 && (
            <p
              style={{
                textAlign: "center",
                color: "#888",
                padding: "24px 0",
                fontSize: 14,
              }}
            >
              You've seen all products! 🌈
            </p>
          )}
        </div>
      </section>

      {/* Mobile filter button (for sidebar) */}
      <div className="btn-sidebar-style2">
        <button>
          <i className="icon icon-sidebar-2" />
        </button>
      </div>
      <ProductFilters
        open={filterOpen}
        onClose={() => {
          setFilterOpen(false);
          // Reload page when filter drawer closes if filters were applied
          setTimeout(() => {
            window.location.reload();
          }, 50);
        }}
        selectedFilters={activeFilters}
        onFilterChange={(f) =>
          handleFilterChange({
            product_types: f.product_types || [],
            genders: f.genders || [],
            ages: f.ages || [],
            collections: f.collections || [],
            sizes: f.sizes || [],
            colors: f.colors || [],
            priceRange: [
              Number.isFinite(Number(f.priceMin))
                ? Number(f.priceMin)
                : priceDefaults[0],
              Number.isFinite(Number(f.priceMax))
                ? Number(f.priceMax)
                : priceDefaults[1],
            ],
          })
        }
      />
    </>
  );
};

export default Products;
